import { Button, makeStyles } from '@material-ui/core';
import { handleDownload } from '../helpers/handleDownload';
import { ReactComponent as DownloadIcon } from '../icons/download.svg';
import { ReactComponent as RawIcon } from '../icons/raw.svg';
import { ReactComponent as ReportIcon } from '../icons/report.svg';
import CustomTooltip from './CustomTooltip';

interface FileCardProps {
    variant: 'report' | 'raw';
    url: string;
    title: string;
    date: Date;
    isAdmin: boolean;
    fileName?: string;
}

const FileCard = ({
    variant,
    url,
    title,
    date,
    isAdmin = false,
    fileName,
}: FileCardProps) => {
    const classes = useStyles();

    return (
        <div className={classes.root}>
            <div className={classes.decorator} />
            <div className={classes.content}>
                <div className={classes.top}>
                    <div className={classes.topTexts}>
                        <CustomTooltip
                            title={title}
                            placement="top"
                            variant="dark"
                        >
                            <h3 className={classes.title}>{title}</h3>
                        </CustomTooltip>
                        {fileName ? (
                            <CustomTooltip
                                title={fileName}
                                placement="right"
                                variant="dark"
                            >
                                <span className={classes.fileName}>
                                    File: {fileName || 'N/A'}
                                </span>
                            </CustomTooltip>
                        ) : (
                            <span className={classes.fileName}>
                                File: {fileName || 'N/A'}
                            </span>
                        )}
                        <span className={classes.date}>
                            Release date: {new Date(date).toLocaleDateString()}
                        </span>
                    </div>
                    <div className={classes.icon}>
                        {variant === 'report' && <ReportIcon />}
                        {variant === 'raw' && <RawIcon />}
                    </div>
                </div>
                <div className={classes.buttons}>
                    <Button
                        startIcon={
                            <DownloadIcon className={classes.downloadIcon} />
                        }
                        className={classes.reportsButton}
                        onClick={() => handleDownload(url, isAdmin, true)}
                    >
                        Download
                    </Button>
                </div>
            </div>
        </div>
    );
};

const useStyles = makeStyles((theme) => ({
    root: {
        position: 'relative',
        display: 'flex',
        width: '330px',
        height: '210px',
        paddingRight: '15px',
        alignItems: 'center',
        gap: '17px',
        borderRadius: '12px',
        background: theme.palette.primary.background,
        boxShadow: `0px 4px 16px -2px rgba(0, 0, 0, 0.07)`,
    },
    decorator: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '12px',
        height: '100%',
        backgroundColor: theme.palette.primary.light,
        borderTopLeftRadius: '12px',
        borderBottomLeftRadius: '12px',
    },
    content: {
        flex: 1,
        height: '100%',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        alignItems: 'stretch',
        gap: '12px',
        paddingLeft: '32px',
    },
    top: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        alignSelf: 'stretch',
        justifyContent: 'space-between',
    },
    topTexts: {
        display: 'flex',
        width: '200px',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: '6px',
        flex: 1,
    },
    title: {
        color: theme.palette.primary.main,
        fontFamily: 'Poppins',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '150%',
        letterSpacing: '0.07px',
        margin: 0,
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    date: {
        alignSelf: 'stretch',
        color: theme.palette.primary.main,
        fontFamily: 'Noto Sans',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '150%',
        letterSpacing: '-0.252px',
    },
    fileName: {
        alignSelf: 'stretch',
        color: theme.palette.primary.main,
        fontFamily: 'Noto Sans',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: '150%',
        letterSpacing: '-0.252px',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    icon: {
        width: '60px',
        height: '60px',
        marginBottom: 'auto',
    },
    buttons: {
        display: 'flex',
        padding: 0,
        alignItems: 'center',
        justifyContent: 'flex-start',
    },
    downloadIcon: {
        color: theme.palette.primary.main,
        scale: '0.8',
    },
    reportsButton: {
        display: 'flex',
        height: 32,
        padding: '10px 14px',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 10,
        borderRadius: 25,
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.primary.main,
        fontFamily: 'Noto Sans',
        fontSize: 14,
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '150%',
        letterSpacing: -0.252,
        textTransform: 'none',
    },
}));

export default FileCard;
